import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { landingPagesService } from '@/services/landingPagesService';
import type { LandingFormConfig } from '@/services/landingPagesService';

/**
 * Choose which of a client's forms their landing pages must use.
 *
 * ⚠ THIS IS THE ONE DECISION THE GENERATOR REFUSES TO MAKE. Detection resolves
 * a site with exactly one form on its own. A site with several is a hard stop:
 * it can see that three forms exist, it cannot see which one is wired to the
 * client's inbox, their CRM or their call tracking. It used to take the
 * most-used one, and on a third of audited sites that is a coin flip — the
 * cost being submissions routed to the wrong notification, invisible until a
 * client asks where their leads went.
 *
 * Until this screen existed the answer was a spreadsheet handed to
 * engineering, so the person who could tell which form is real could not
 * record it, and the person recording it could not tell.
 */

const STATUS_COPY: Record<
  LandingFormConfig['status'],
  { tone: string; title: string; body: string }
> = {
  configured: {
    tone: 'border-l-4 border-l-coral bg-coral-light',
    title: 'A form is recorded',
    body: 'Landing pages for this client embed the form below.',
  },
  auto: {
    tone: 'border-l-4 border-l-teal bg-teal-light',
    title: 'Nothing to do',
    body:
      'Their site runs exactly one form the generator recognises, so it uses ' +
      'that one automatically. Record a choice only if you want to override it.',
  },
  ambiguous: {
    tone: 'border-l-4 border-l-destructive bg-destructive/5',
    title: 'A choice is needed before a page can be built',
    body:
      'Their site runs more than one form. Pick the one already wired to ' +
      'their lead routing — the generator will not guess, because guessing ' +
      'wrong sends submissions somewhere nobody is watching.',
  },
  none: {
    tone: 'border-l-4 border-l-destructive bg-destructive/5',
    title: 'No recognised form on their site',
    body:
      'Their form is custom-built or embedded from a service the generator ' +
      'cannot host. That needs a conversation with the client about using a ' +
      'supported form, rather than a choice here.',
  },
};

interface Props {
  tenantId: string;
  /** Shown in the audit trail, so a choice can be asked about later. */
  confirmedBy?: string;
}

export default function FormPicker({ tenantId, confirmedBy = '' }: Props) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['landing-form-config', tenantId],
    queryFn: () => landingPagesService.formConfig(tenantId),
    enabled: !!tenantId,
    // ⚠ This fetches the client's live website to list their forms. It is not
    // a read anyone should repeat on focus.
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const [choice, setChoice] = useState('');
  const [manualProvider, setManualProvider] = useState('');
  const [manualId, setManualId] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!data) return;
    setNote(data.configured?.note ?? '');
    if (data.configured) {
      setChoice(`${data.configured.provider}:${data.configured.form_id}`);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const [provider, form_id] = choice
        ? choice.split(':')
        : [manualProvider, manualId.trim()];
      return landingPagesService.saveFormConfig(tenantId, {
        provider,
        form_id,
        note,
        confirmed_by: confirmedBy,
      });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['landing-form-config', tenantId] }),
  });

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">
        Checking {tenantId} for forms. This reads their live site, so give it a
        moment.
      </p>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive">
        Could not read the form configuration: {String((error as Error)?.message)}
      </p>
    );
  }
  if (!data) return null;

  const copy = STATUS_COPY[data.status];
  const canSave = !!choice || (!!manualProvider && !!manualId.trim());

  return (
    <div className="max-w-2xl space-y-6">
      <div className={`rounded-md p-4 ${copy.tone}`}>
        <p className="text-sm font-medium">{copy.title}</p>
        <p className="mt-1 text-sm text-teal-dark">{copy.body}</p>
      </div>

      {/* ⚠ THE FAILURE WORTH SURFACING LOUDEST: a recorded form that is no
          longer anywhere on their site. Every page built since is collecting
          nothing, and the usual first sign is the client asking where their
          leads went. */}
      {data.configured_still_present === false && (
        <div className="rounded-md border-l-4 border-l-destructive bg-destructive/5 p-4">
          <p className="text-sm font-medium">
            The recorded form is no longer on their site
          </p>
          <p className="mt-1 text-sm text-teal-dark">
            {data.configured?.provider} form {data.configured?.form_id} was not
            found on any page we checked. Pages built with it may be collecting
            nothing. Re-confirm against the list below.
          </p>
        </div>
      )}

      {data.candidates.length > 0 && (
        <fieldset>
          <legend className="mb-2 text-sm font-medium">
            Forms found on {tenantId}
          </legend>
          <p className="mb-3 text-sm text-muted-foreground">
            Open each one in their WordPress admin and look at where its
            submissions go — the Mail tab in Contact Form 7, Settings then
            Notifications in Gravity Forms. The right form is the one that
            reaches the client. Do not pick by which appears most: that is what
            the generator used to do, and it was wrong on a third of sites.
          </p>
          <div className="space-y-2">
            {data.candidates.map((c) => {
              const value = `${c.provider}:${c.form_id}`;
              return (
                <label
                  key={value}
                  className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm transition-colors ${
                    choice === value
                      ? 'border-coral bg-coral-light'
                      : 'border-border hover:bg-teal-light'
                  }`}
                >
                  <input
                    type="radio"
                    name="landing-form"
                    value={value}
                    checked={choice === value}
                    onChange={() => setChoice(value)}
                  />
                  <span className="font-medium">{c.provider_label}</span>
                  <span className="tabular-nums text-muted-foreground">
                    id {c.form_id}
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    on {c.occurrences} page{c.occurrences === 1 ? '' : 's'}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      <details className="rounded-md border border-border p-3">
        <summary className="cursor-pointer text-sm font-medium">
          Enter a form by hand
        </summary>
        <p className="mt-2 text-sm text-muted-foreground">
          For a form that exists but was not detected, for example one that
          only appears on a page we did not sample.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <select
            value={manualProvider}
            onChange={(e) => {
              setManualProvider(e.target.value);
              setChoice('');
            }}
            className="rounded-md border border-border px-2 py-1.5 text-sm"
          >
            <option value="">Choose a plugin</option>
            {data.providers.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
          <input
            value={manualId}
            onChange={(e) => {
              setManualId(e.target.value);
              setChoice('');
            }}
            placeholder="Form id"
            className="rounded-md border border-border px-2 py-1.5 text-sm"
          />
        </div>
      </details>

      <div>
        <label htmlFor="form-note" className="text-sm font-medium">
          How you confirmed it
        </label>
        {/* ⚠ NOT DECORATION. Months later the question is whether the wrong
            form was chosen or the right form stopped working, and those have
            completely different fixes. One line here answers it in seconds. */}
        <p className="mb-2 text-sm text-muted-foreground">
          One line is enough, for example: Mail tab goes to leads@client.com,
          and it is the form on the page their ads point at.
        </p>
        <textarea
          id="form-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-border p-2 text-sm"
        />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!canSave || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-md bg-coral px-4 py-2 text-sm font-medium text-white hover:bg-coral-hover disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save this form'}
        </button>
        {save.isSuccess && (
          <span className="text-sm text-teal-dark">
            Saved. Pages built from now on use it.
          </span>
        )}
        {save.isError && (
          <span className="text-sm text-destructive">
            {String((save.error as Error)?.message)}
          </span>
        )}
      </div>

      {data.configured?.confirmed_at && (
        <p className="text-xs text-muted-foreground">
          Last confirmed {new Date(data.configured.confirmed_at).toLocaleString()}
          {data.configured.confirmed_by
            ? ` by ${data.configured.confirmed_by}`
            : ''}
          .
        </p>
      )}
    </div>
  );
}
